import { create } from "zustand";
import { apiFetch } from "@/lib/api-fetch";

export interface Episode {
  id: string;
  projectId: string;
  title: string;
  sequence: number;
  idea: string | null;
  /**
   * 剧本**正文**。
   *
   * ⚠️ 轻量接口（`fetchEpisodeMetas`）不下发这个字段 —— 判空/取长度请用
   * `hasScript` / `scriptLength`，不要读 `script`（会得到 `undefined`）。
   */
  script: string | null;
  /** 剧本正文字数（`fetchEpisodeMetas` 给的派生字段）。 */
  scriptLength?: number;
  /** 是否已有剧本（`fetchEpisodeMetas` 给的派生字段）。 */
  hasScript?: boolean;
  description: string | null;
  keywords: string | null;
  status: string;
  generationMode: "keyframe" | "reference";
  /**
   * 🔴 2026-10-17：本集**目标时长（秒）**，分镜的时长预算依据。
   *
   * 从分镜 **V6** 起必填（预检 `STORYBOARD_DURATION_BUDGET_MISSING`）。
   * 0 / null = 未设置 —— 分镜会回落到 `projects.target_duration`，两者都为 0 才被拦。
   * 预算允许按剧情与对白浮动（下浮 10% / 上浮 25%，见 `lib/ai/duration-budget`）。
   */
  targetDuration?: number | null;
  /** dialogue | compact | artistic. Numeric duration is derived from this and script beats. */
  pacingProfile?: string | null;
  finalVideoUrl: string | null;
  previewImages?: string[];
  createdAt: string | number;
  updatedAt: string | number;
}

/** 判断某集是否有剧本 —— 兼容完整接口（`script`）与轻量接口（`scriptLength`）。 */
export function episodeHasScript(ep: Pick<Episode, "script" | "scriptLength" | "hasScript">): boolean {
  if (typeof ep.hasScript === "boolean") return ep.hasScript;
  if (typeof ep.scriptLength === "number") return ep.scriptLength > 0;
  return (ep.script ?? "").trim().length > 0;
}

interface EpisodeStore {
  episodes: Episode[];
  loading: boolean;
  /**
   * 拉取**完整**分集列表（含 `script` 正文 + `previewImages`）。
   * 只有真正渲染分集卡片 / 读剧本正文的页面才需要它。
   */
  fetchEpisodes: (projectId: string) => Promise<void>;
  /**
   * 拉取**轻量**分集元信息（不含 `script` / `idea` / `outline` 正文，不含 `previewImages`）。
   *
   * 🔴 2026-09-17「菜单跳转非常慢」优化新增。
   * 实测：完整接口 139.3 KB / 267 ms，其中 `script` 独占 101.7 KB（73%）；
   * 轻量接口 3.3 KB / 11 ms —— **体积降 97.7%，耗时降 96%**。
   *
   * 适用于只想拿「集名 + 集 id + 有无剧本」的调用点（分镜页集列表、各种下拉）。
   * 写入同一份 `episodes` state —— 调用方无感，但**读 `script` 会得到 undefined**，
   * 判空请改用 `episodeHasScript()`。
   */
  fetchEpisodeMetas: (projectId: string) => Promise<void>;
  createEpisode: (projectId: string, data: { title: string; description?: string; keywords?: string; targetDuration?: number; pacingProfile?: string }) => Promise<Episode>;
  deleteEpisode: (projectId: string, episodeId: string) => Promise<void>;
  updateEpisode: (
    projectId: string,
    episodeId: string,
    patch: Partial<Pick<Episode, "title" | "description" | "keywords" | "idea" | "script" | "status" | "generationMode" | "targetDuration" | "pacingProfile">>
  ) => Promise<void>;
  reorderEpisodes: (projectId: string, orderedIds: string[]) => Promise<void>;
}

/**
 * 轻量模式的模块级缓存 —— 分镜页与其他页面会反复挂载，同一个项目不必重复拉。
 * 30s TTL，与 `project-store` 的缓存策略保持一致。
 */
const META_TTL_MS = 30_000;
let metaCacheKey: string | null = null;
let metaCacheAt = 0;
let metaCache: Episode[] = [];
let metaInflight: Promise<Episode[]> | null = null;

/** 供增删改后主动失效缓存。 */
export function invalidateEpisodeMetaCache() {
  metaCacheKey = null;
  metaCacheAt = 0;
}

/**
 * 完整模式的模块级缓存（2026-10-03「切换菜单特别慢」优化）。
 *
 * 🔴 改造前 `fetchEpisodes` 每次进分集页都全量重拉（139 KB+，含 script 正文）
 * 且全屏转圈等网络 —— 页面间来回切一次就烧一次。与 `fetchEpisodeMetas` 同款策略：
 * 30s TTL + inflight 并发去重；增删改后 `invalidateEpisodeCache()` 失效。
 * 图片加载由卡片侧 thumbUrl + loading="lazy" 承担（素材库同款三板斧）。
 */
const FULL_TTL_MS = 30_000;
let fullCacheKey: string | null = null;
let fullCacheAt = 0;
let fullCache: Episode[] = [];
let fullInflight: Promise<Episode[]> | null = null;

/** 供增删改后主动失效完整缓存（连同轻量缓存一起，保证两份视图一致）。 */
export function invalidateEpisodeCache() {
  fullCacheKey = null;
  fullCacheAt = 0;
  invalidateEpisodeMetaCache();
}

export const useEpisodeStore = create<EpisodeStore>((set) => ({
  episodes: [],
  loading: false,

  fetchEpisodes: async (projectId: string) => {
    // 命中缓存：直接采用，零网络等待（切换菜单"秒开"的关键路径）
    if (fullCacheKey === projectId && Date.now() - fullCacheAt < FULL_TTL_MS) {
      set({ episodes: fullCache, loading: false });
      return;
    }
    set({ loading: true });
    // 并发去重：同项目同时挂载只发一次请求
    if (!fullInflight) {
      fullInflight = (async () => {
        const res = await apiFetch(`/api/projects/${projectId}/episodes`);
        const data = (await res.json()) as Episode[];
        return Array.isArray(data) ? data : [];
      })();
    }
    try {
      const data = await fullInflight;
      fullCacheKey = projectId;
      fullCacheAt = Date.now();
      fullCache = data;
      set({ episodes: data, loading: false });
    } catch (err) {
      // 🔴 失败必须复位 loading，否则界面永久转圈
      set({ loading: false });
      throw err;
    } finally {
      fullInflight = null;
    }
  },

  fetchEpisodeMetas: async (projectId: string) => {
    // 命中缓存：直接采用（仍走 set 以保持引用语义与其它页面同步）
    if (metaCacheKey === projectId && Date.now() - metaCacheAt < META_TTL_MS) {
      set({ episodes: metaCache, loading: false });
      return;
    }
    set({ loading: true });
    // 并发去重：多个组件同时挂载只发一次请求
    if (!metaInflight) {
      metaInflight = (async () => {
        const res = await apiFetch(`/api/projects/${projectId}/episodes?lite=1`);
        const data = (await res.json()) as Episode[];
        return Array.isArray(data) ? data : [];
      })();
    }
    try {
      const data = await metaInflight;
      metaCacheKey = projectId;
      metaCacheAt = Date.now();
      metaCache = data;
      set({ episodes: data, loading: false });
    } catch (err) {
      // 🔴 失败必须复位 loading，否则界面永久转圈
      set({ loading: false });
      throw err;
    } finally {
      metaInflight = null;
    }
  },

  createEpisode: async (projectId, data) => {
    const res = await apiFetch(`/api/projects/${projectId}/episodes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const created: Episode = await res.json();
    // 列表变了 → 轻量模式缓存失效，否则下次进分镜页会看到旧的集列表
    invalidateEpisodeCache();
    set((state) => ({ episodes: [...state.episodes, created] }));
    return created;
  },

  deleteEpisode: async (projectId: string, episodeId: string) => {
    await apiFetch(`/api/projects/${projectId}/episodes/${episodeId}`, {
      method: "DELETE",
    });
    invalidateEpisodeCache();
    set((state) => ({
      episodes: state.episodes.filter((e) => e.id !== episodeId),
    }));
  },

  updateEpisode: async (projectId, episodeId, patch) => {
    const res = await apiFetch(
      `/api/projects/${projectId}/episodes/${episodeId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }
    );
    const updated: Episode = await res.json();
    invalidateEpisodeCache();
    set((state) => ({
      episodes: state.episodes.map((e) =>
        e.id === episodeId ? { ...e, ...updated } : e
      ),
    }));
  },

  reorderEpisodes: async (projectId, orderedIds) => {
    await apiFetch(`/api/projects/${projectId}/episodes/reorder`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderedIds }),
    });
    invalidateEpisodeCache();
    // Update sequences in local state based on the new order
    set((state) => ({
      episodes: state.episodes
        .map((e) => {
          const idx = orderedIds.indexOf(e.id);
          return idx >= 0 ? { ...e, sequence: idx + 1 } : e;
        })
        .sort((a, b) => a.sequence - b.sequence),
    }));
  },
}));
